import { useState } from "react";
import { toast } from "sonner";

interface UseFileDownloadReturn {
  downloadFile: (url: string, filename: string) => Promise<void>;
  isDownloading: boolean;
  error: string | null;
}

interface UseFileDownloadOptions {
  // i18n error messages (optional)
  errorMessages?: {
    noUrl?: string;
    noUrlAlert?: string;
    downloadFailed?: string;
    downloadError?: string;
    downloadFailedAlert?: string;
  };
}

/**
 * File download hook with blob-based download for cross-origin files
 * Supports i18n through optional error message parameters
 *
 * @example
 * ```tsx
 * const { downloadFile, isDownloading } = useFileDownload({
 *   errorMessages: {
 *     noUrl: 'Download URL not provided',
 *     downloadFailed: 'File download failed'
 *   }
 * });
 *
 * const handleDownload = () => {
 *   downloadFile(pdfUrl, 'quote.pdf');
 * };
 * ```
 */
export function useFileDownload(
  options: UseFileDownloadOptions = {},
): UseFileDownloadReturn {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    noUrl = "Download URL not provided",
    noUrlAlert = "Cannot download file",
    downloadFailed = "File download failed",
    downloadError = "An error occurred during file download",
    downloadFailedAlert = "File download failed",
  } = options.errorMessages ?? {};

  const downloadFile = async (url: string, filename: string) => {
    if (!url) {
      setError(noUrl);
      toast.error(noUrlAlert);
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      // Fetch the file as a blob to force download instead of opening
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(downloadFailed);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : downloadError;
      console.error("File download error:", err);
      setError(errorMessage);
      toast.error(downloadFailedAlert);
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadFile,
    isDownloading,
    error,
  };
}
